import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CaretLeft,
  CaretRight,
  Plus,
  CalendarBlank,
} from "@phosphor-icons/react";
import PlanningEventModal from "./PlanningEventModal";
import {
  TYPE_COLORS,
  TYPE_BORDER,
  monthGrid,
  startOfWeek,
  startOfMonth,
  endOfMonth,
  endOfWeek,
  sameDay,
  fmtMonthYear,
  fmtTime,
  fmtRange,
  groupByDay,
} from "./calendarUtils";

const MS_DAY = 86_400_000;

function navByPeriod(date, mode, direction) {
  const d = new Date(date);
  if (mode === "month") {
    d.setMonth(d.getMonth() + direction);
  } else {
    d.setDate(d.getDate() + 7 * direction);
  }
  return d;
}

function MonthView({ cursor, eventsByDay, locale, onPickDay, onPickEvent }) {
  const { t } = useTranslation();
  const days = useMemo(() => monthGrid(cursor), [cursor]);
  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);
  const today = new Date();
  const weekdays = useMemo(() => {
    const ref = startOfWeek(new Date());
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(ref.getTime() + i * MS_DAY);
      return d.toLocaleDateString(locale, { weekday: "short" });
    });
  }, [locale]);

  return (
    <div data-testid="calendar-month">
      <div className="grid grid-cols-7 border-b border-slate-200">
        {weekdays.map((w, i) => (
          <div
            key={i}
            className="px-2 py-2 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500"
          >
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d) => {
          const key = new Date(
            d.getFullYear(),
            d.getMonth(),
            d.getDate(),
          ).toISOString();
          const evts = eventsByDay.get(key) || [];
          const isCurMonth = d >= monthStart && d < monthEnd;
          const isToday = sameDay(d, today);
          return (
            <button
              type="button"
              key={key}
              data-testid={`calendar-cell-${d.toISOString().slice(0, 10)}`}
              onClick={() => onPickDay(d)}
              className={`group/cell relative min-h-[110px] border-b border-r border-slate-200 p-2 text-left transition-colors hover:bg-slate-50 ${
                isCurMonth ? "bg-white" : "bg-slate-50/40"
              }`}
            >
              <span
                className={`inline-flex h-6 min-w-[24px] items-center justify-center rounded-full px-1 text-xs ${
                  isToday
                    ? "bg-slate-900 text-white font-bold"
                    : isCurMonth
                      ? "text-slate-700"
                      : "text-slate-400"
                }`}
              >
                {d.getDate()}
              </span>
              <ul className="mt-1 space-y-1">
                {evts.slice(0, 3).map((e) => (
                  <li
                    key={e.id}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      onPickEvent(e);
                    }}
                    className={`truncate rounded-md border px-1.5 py-0.5 text-[11px] font-medium ${
                      TYPE_COLORS[e.type] || TYPE_COLORS.custom
                    }`}
                  >
                    <span className="mr-1 opacity-70">
                      {fmtTime(e.start_at, locale)}
                    </span>
                    {e.title}
                  </li>
                ))}
                {evts.length > 3 && (
                  <li className="text-[10px] text-slate-500">
                    +{evts.length - 3} {t("planning.more")}
                  </li>
                )}
              </ul>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function WeekView({ cursor, eventsByDay, locale, onPickDay, onPickEvent }) {
  const start = startOfWeek(cursor);
  const days = Array.from(
    { length: 7 },
    (_, i) => new Date(start.getTime() + i * MS_DAY),
  );
  const today = new Date();

  return (
    <div data-testid="calendar-week" className="grid grid-cols-7">
      {days.map((d) => {
        const key = new Date(
          d.getFullYear(),
          d.getMonth(),
          d.getDate(),
        ).toISOString();
        const evts = eventsByDay.get(key) || [];
        const isToday = sameDay(d, today);
        return (
          <div
            key={key}
            className="border-r border-slate-200 last:border-r-0 min-h-[480px]"
          >
            <button
              type="button"
              onClick={() => onPickDay(d)}
              data-testid={`week-day-${d.toISOString().slice(0, 10)}`}
              className="w-full text-left px-3 py-3 border-b border-slate-200 hover:bg-slate-50"
            >
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                {d.toLocaleDateString(locale, { weekday: "short" })}
              </p>
              <p
                className={`mt-0.5 text-2xl font-heading font-bold ${
                  isToday ? "text-slate-900" : "text-slate-700"
                }`}
              >
                {d.getDate()}
              </p>
            </button>
            <ul className="px-2 py-2 space-y-2">
              {evts.map((e) => (
                <li key={e.id}>
                  <button
                    type="button"
                    onClick={() => onPickEvent(e)}
                    className={`w-full text-left rounded-lg border-l-4 ${
                      TYPE_BORDER[e.type] || TYPE_BORDER.custom
                    } bg-white shadow-sm p-2 hover:shadow transition-shadow`}
                  >
                    <p className="text-xs font-semibold text-slate-900 truncate">
                      {e.title}
                    </p>
                    <p className="text-[11px] text-slate-500">
                      {fmtRange(e.start_at, e.end_at, locale)}
                    </p>
                    {e.location && (
                      <p className="text-[11px] text-slate-500 truncate">
                        {e.location}
                      </p>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Self-contained calendar: handles fetch, view toggle, navigation,
 * event creation/edition via modal.
 *
 * Props:
 *   api: { list({start,end}), create(data), update(id,patch), remove(id) }
 *   canEditVisibility: shows the visible_to_model toggle in the modal
 */
export default function PlanningCalendar({ api, canEditVisibility = true, testId }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "fr";
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(() => new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pickedDate, setPickedDate] = useState(null);

  const range = useMemo(() => {
    if (view === "month") {
      const s = startOfWeek(startOfMonth(cursor));
      const e = new Date(s.getTime() + 42 * MS_DAY);
      return {
        start: s.toISOString(),
        end: e.toISOString(),
      };
    }
    const s = startOfWeek(cursor);
    const e = endOfWeek(cursor);
    return { start: s.toISOString(), end: e.toISOString() };
  }, [view, cursor]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.list(range);
      setEvents(data);
    } finally {
      setLoading(false);
    }
  }, [api, range]);

  useEffect(() => {
    load();
  }, [load]);

  const eventsByDay = useMemo(() => groupByDay(events), [events]);

  const onPickDay = (d) => {
    setEditing(null);
    setPickedDate(d);
    setModalOpen(true);
  };
  const onPickEvent = (e) => {
    setEditing(e);
    setPickedDate(null);
    setModalOpen(true);
  };
  const newEvent = () => {
    setEditing(null);
    setPickedDate(new Date());
    setModalOpen(true);
  };

  const handleSave = useCallback(
    async (payload) => {
      if (editing) {
        await api.update(editing.id, payload);
      } else {
        await api.create(payload);
      }
      await load();
    },
    [editing, api, load],
  );

  const handleDelete = useCallback(
    async (id) => {
      await api.remove(id);
      await load();
    },
    [api, load],
  );

  return (
    <div data-testid={testId || "planning-calendar"} className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="calendar-prev"
            onClick={() => setCursor((c) => navByPeriod(c, view, -1))}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 hover:border-slate-900"
            aria-label={t("planning.prev")}
          >
            <CaretLeft size={14} weight="bold" />
          </button>
          <button
            type="button"
            data-testid="calendar-today"
            onClick={() => setCursor(new Date())}
            className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-slate-900"
          >
            {t("planning.today")}
          </button>
          <button
            type="button"
            data-testid="calendar-next"
            onClick={() => setCursor((c) => navByPeriod(c, view, 1))}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 hover:border-slate-900"
            aria-label={t("planning.next")}
          >
            <CaretRight size={14} weight="bold" />
          </button>
          <p className="ml-3 font-heading text-xl font-bold text-slate-900 capitalize">
            {fmtMonthYear(cursor, locale)}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full border border-slate-200 overflow-hidden">
            {["week", "month"].map((m) => (
              <button
                key={m}
                type="button"
                data-testid={`calendar-view-${m}`}
                onClick={() => setView(m)}
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                  view === m
                    ? "bg-slate-900 text-white"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                {t(`planning.view.${m}`)}
              </button>
            ))}
          </div>
          <button
            type="button"
            data-testid="calendar-new-event"
            onClick={newEvent}
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
          >
            <Plus size={13} weight="bold" />
            {t("planning.new_event")}
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {events.length === 0 && !loading && (
          <div className="absolute inset-0 pointer-events-none" />
        )}
        {view === "month" ? (
          <MonthView
            cursor={cursor}
            eventsByDay={eventsByDay}
            locale={locale}
            onPickDay={onPickDay}
            onPickEvent={onPickEvent}
          />
        ) : (
          <WeekView
            cursor={cursor}
            eventsByDay={eventsByDay}
            locale={locale}
            onPickDay={onPickDay}
            onPickEvent={onPickEvent}
          />
        )}
      </div>

      {events.length === 0 && !loading && (
        <p className="text-center text-sm text-slate-500 py-4 inline-flex items-center gap-2 justify-center w-full">
          <CalendarBlank size={14} weight="regular" />
          {t("planning.empty")}
        </p>
      )}

      <PlanningEventModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        initialEvent={editing}
        initialDate={pickedDate}
        onSave={handleSave}
        onDelete={handleDelete}
        canTogglevisibility={canEditVisibility}
      />
    </div>
  );
}
